import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  createStyles,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  withStyles,
  WithStyles
} from "@material-ui/core";
import { faPlug } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";

import alvysApi, {
  AlvysConnection,
  AlvysSecret
} from "../../../../../../api/factoring/alvys";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "../../../../../../components/Dialog";
import PasswordInput from "../../../../../../components/inputs/PasswordInput";
import copyText from "../../../../../../helpers/copyText";
import formatDate from "../../../../../../helpers/format/formatDate";
import getErrorMessage from "../../../../../../helpers/getErrorMessage";
import colors from "../../../../../../theme/colors";
import {
  AlvysFormValues,
  changedValues,
  clientIdMissing,
  clientSecretMissing,
  expiryNote,
  formValuesFrom,
  hasCredentials,
  isNewKey,
  KEY_STATUS,
  maskedSecret,
  NEW_KEY_EXPIRY_NOTE
} from "./helpers";

const styles = () =>
  createStyles({
    headerRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: 16,
      "& > *:not(:last-child)": {
        marginRight: 10
      }
    },
    title: {
      fontWeight: 600
    },
    chip: {
      color: colors.white,
      fontWeight: 600,
      letterSpacing: 0.5
    },
    error: {
      color: colors.danger,
      marginBottom: 8
    },
    help: {
      color: colors.grayText,
      fontStyle: "italic",
      marginBottom: 8
    },
    sectionHeader: {
      fontWeight: 600,
      marginTop: 20,
      marginBottom: 4
    },
    actions: {
      marginTop: 12,
      display: "flex",
      justifyContent: "flex-end",
      "& > *:not(:last-child)": {
        marginRight: 8
      }
    },
    eventChip: {
      marginTop: 8,
      marginRight: 8
    },
    detailGrid: {
      marginTop: 12
    },
    detailLabel: {
      color: colors.grayText,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.5
    },
    detailValue: {
      color: colors.textColor,
      wordBreak: "break-word"
    },
    removeButton: {
      backgroundColor: colors.danger,
      color: colors.white
    }
  });

const LOAD_ERROR = "Unable to load the Alvys settings. Please try again.";
const SAVE_ERROR = "Unable to save the Alvys settings. Please try again.";
const UNAVAILABLE_NOTE =
  "Alvys is switched off for this server (ALVYS_ENABLED), so no loads sync and it can't be switched on here.";
const REMOVE_WARNING =
  "This deletes the client's Alvys API key and webhook signing secret. Loads stop syncing, and Alvys webhook deliveries are refused, until new ones are entered.";

type SecretName = "clientSecret" | "webhookSecret";

const NOT_REPLACING: Record<SecretName, boolean> = {
  clientSecret: false,
  webhookSecret: false
};

interface SecretFieldProps {
  label: string;
  secret: AlvysSecret;
  value: string;
  isReplacing: boolean;
  onReplace: (isReplacing: boolean) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
}

const SecretField = ({
  label,
  secret,
  value,
  isReplacing,
  onReplace,
  onChange,
  error
}: SecretFieldProps): JSX.Element => {
  if (secret.configured && !isReplacing) {
    return (
      <Grid container spacing={8} alignItems="flex-end">
        <Grid item xs>
          <TextField
            fullWidth
            label={label}
            value={maskedSecret(secret)}
            error={Boolean(error)}
            helperText={error}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "aria-label": label, readOnly: true }}
          />
        </Grid>
        <Grid item>
          <Button
            type="button"
            size="small"
            aria-label={`Replace ${label}`}
            onClick={(): void => onReplace(true)}
          >
            Replace
          </Button>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container spacing={8} alignItems="flex-end">
      <Grid item xs>
        <PasswordInput
          fullWidth
          showEndPasswordIcon
          autoFocus={isReplacing}
          label={label}
          placeholder="Paste from Alvys"
          value={value}
          onChange={onChange}
          autoComplete="new-password"
          error={Boolean(error)}
          helperText={error}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "aria-label": label }}
        />
      </Grid>
      {isReplacing && (
        <Grid item>
          <Button
            type="button"
            size="small"
            aria-label={`Keep the current ${label}`}
            onClick={(): void => onReplace(false)}
          >
            Cancel
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

interface Props extends WithStyles<typeof styles> {
  factoringId: string;
  openSnackbar: (variant: string, message: string) => void;
}

const AlvysIntegrationPanel = ({
  classes,
  factoringId,
  openSnackbar
}: Props): JSX.Element => {
  const [connection, setConnection] = useState<AlvysConnection | null>(null);
  const [values, setValues] = useState<AlvysFormValues | null>(null);
  const [replacing, setReplacing] = useState(NOT_REPLACING);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const activeFactoringId = useRef(factoringId);
  activeFactoringId.current = factoringId;

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    setConnection(null);
    setValues(null);
    setReplacing(NOT_REPLACING);
    setError(null);

    alvysApi
      .getAlvysConnection(factoringId)
      .then((data: AlvysConnection): void => {
        if (isActive) {
          setConnection(data);
          setValues(formValuesFrom(data));
        }
      })
      .catch((err: unknown): void => {
        if (isActive) setError(getErrorMessage(err, LOAD_ERROR));
      })
      .finally((): void => {
        if (isActive) setLoading(false);
      });

    return (): void => {
      isActive = false;
    };
  }, [factoringId]);

  const run = async (
    request: () => Promise<AlvysConnection>,
    success: string,
    resetForm: boolean
  ): Promise<void> => {
    const requestedFor = factoringId;
    setSaving(true);
    setError(null);
    try {
      const data = await request();
      if (activeFactoringId.current !== requestedFor) return;
      setConnection(data);
      if (resetForm) {
        setValues(formValuesFrom(data));
        setReplacing(NOT_REPLACING);
      }
      openSnackbar("success", success);
    } catch (err) {
      if (activeFactoringId.current !== requestedFor) return;
      const message = getErrorMessage(err, SAVE_ERROR);
      setError(message);
      openSnackbar("error", message);
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  if (isLoading || !connection || !values) {
    return (
      <Card>
        <CardContent>
          <div className={classes.headerRow}>
            <FontAwesomeIcon icon={faPlug} color={colors.grayText} />
            <Typography variant="h6" className={classes.title}>
              Alvys Integration
            </Typography>
          </div>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography className={classes.error} role="alert">
              {error || LOAD_ERROR}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  const update = changedValues(connection, values);
  const isDirty = Object.keys(update).length > 0;
  const missingClientId = clientIdMissing(connection, values);
  const missingClientSecret = clientSecretMissing(connection, values);
  const status = KEY_STATUS[connection.key_status];
  const isRenewing = replacing.clientSecret || isNewKey(connection, values);
  const dateField = isRenewing ? "newKeyExpiresOn" : "keyExpiresOn";
  const expiry = isRenewing
    ? NEW_KEY_EXPIRY_NOTE
    : expiryNote(connection.key_status);

  const setValue = (name: keyof AlvysFormValues) => (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = event.target;
    setValues(prev => (prev ? { ...prev, [name]: value } : prev));
  };

  const replace = (name: SecretName) => (isReplacing: boolean): void => {
    setReplacing(prev => ({ ...prev, [name]: isReplacing }));
    if (!isReplacing) {
      setValues(prev => (prev ? { ...prev, [name]: "" } : prev));
    }
  };

  const handleToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): Promise<void> =>
    run(
      () => alvysApi.updateAlvysConnection(factoringId, { enabled: checked }),
      checked ? "Alvys switched on" : "Alvys switched off",
      false
    );

  const handleSave = (): Promise<void> =>
    run(
      () => alvysApi.updateAlvysConnection(factoringId, update),
      "Alvys settings saved",
      true
    );

  const handleRemove = (): Promise<void> =>
    run(
      () => alvysApi.removeAlvysConnection(factoringId),
      "Alvys credentials removed",
      true
    );

  const handleCopy = async (): Promise<void> => {
    try {
      await copyText(connection.webhook_url);
      openSnackbar("success", "Copied to clipboard");
    } catch (err) {
      openSnackbar("error", "Unable to copy the webhook URL.");
    }
  };

  return (
    <Card>
      <CardContent>
        <div className={classes.headerRow}>
          <FontAwesomeIcon
            icon={faPlug}
            color={connection.enabled ? colors.green : colors.grayText}
          />
          <Typography variant="h6" className={classes.title}>
            Alvys Integration
          </Typography>
          <Chip
            label={status.label}
            className={classes.chip}
            style={{ backgroundColor: status.color }}
          />
        </div>

        {error && (
          <Typography className={classes.error} role="alert">
            {error}
          </Typography>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={connection.enabled}
              onChange={handleToggle}
              disabled={
                isSaving || (!connection.available && !connection.enabled)
              }
              value="alvysEnabled"
            />
          }
          label="Sync loads from Alvys"
        />
        <Typography variant="caption" className={classes.help}>
          {connection.available ? (
            <>
              When on, loads that reach Invoiced in this client&apos;s Alvys
              account become funding requests here.
            </>
          ) : (
            UNAVAILABLE_NOTE
          )}
        </Typography>

        <Typography variant="subtitle1" className={classes.sectionHeader}>
          API Key
        </Typography>
        <Typography variant="caption" className={classes.help}>
          Use a read-only Alvys key (invoice:read, load:read, trip:read,
          carrier:read). A new key is checked with Alvys before it is saved.
        </Typography>
        <Grid container spacing={16}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client ID"
              value={values.clientId}
              onChange={setValue("clientId")}
              error={missingClientId}
              helperText={
                missingClientId ? "Enter the key's Client ID." : undefined
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "Client ID" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SecretField
              label="Client Secret"
              secret={connection.client_secret}
              value={values.clientSecret}
              isReplacing={replacing.clientSecret}
              onReplace={replace("clientSecret")}
              onChange={setValue("clientSecret")}
              error={
                missingClientSecret
                  ? "Enter the client secret that goes with this Client ID."
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Key Expires On"
              value={values[dateField]}
              onChange={setValue(dateField)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "Key Expires On" }}
              helperText={expiry.text}
              FormHelperTextProps={{
                style: expiry.color
                  ? { color: expiry.color, fontWeight: 600 }
                  : undefined
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SecretField
              label="Webhook Signing Secret"
              secret={connection.webhook_secret}
              value={values.webhookSecret}
              isReplacing={replacing.webhookSecret}
              onReplace={replace("webhookSecret")}
              onChange={setValue("webhookSecret")}
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          {hasCredentials(connection) && (
            <Button
              type="button"
              size="small"
              color="secondary"
              disabled={isSaving}
              onClick={(): void => setConfirmOpen(true)}
            >
              Remove Credentials
            </Button>
          )}
          <Button
            type="button"
            size="small"
            variant="contained"
            color="primary"
            disabled={
              !isDirty || isSaving || missingClientId || missingClientSecret
            }
            onClick={handleSave}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>

        <Typography variant="subtitle1" className={classes.sectionHeader}>
          Webhook
        </Typography>
        <Typography variant="caption" className={classes.help}>
          In Alvys (Settings → Connections → Webhooks), add a webhook with this
          URL and these events, then enter its signing secret above.
        </Typography>
        <Grid container spacing={8} alignItems="flex-end">
          <Grid item xs>
            <TextField
              fullWidth
              label="Webhook URL"
              value={connection.webhook_url}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                "aria-label": "Webhook URL",
                readOnly: true,
                style: { fontFamily: "monospace" }
              }}
            />
          </Grid>
          <Grid item>
            <Button type="button" size="small" onClick={handleCopy}>
              Copy
            </Button>
          </Grid>
        </Grid>
        <div>
          {connection.webhook_events.map(event => (
            <Chip
              key={event}
              label={event}
              variant="outlined"
              className={classes.eventChip}
            />
          ))}
        </div>

        <Grid container spacing={16} className={classes.detailGrid}>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.detailLabel}>Last Event</Typography>
            <Typography className={classes.detailValue}>
              {connection.last_event_at
                ? formatDate(connection.last_event_at)
                : "No events yet"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.detailLabel}>
              Loads With Sync Problems (7 Days)
            </Typography>
            <Typography className={classes.detailValue}>
              {connection.recent_failures}
            </Typography>
          </Grid>
          {connection.key_rejected_at && (
            <Grid item xs={12} sm={4}>
              <Typography className={classes.detailLabel}>
                Key Rejected At
              </Typography>
              <Typography className={classes.detailValue}>
                {formatDate(connection.key_rejected_at)}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Dialog
          open={isConfirmOpen}
          maxWidth="sm"
          fullWidth
          onClose={(): void => {
            if (!isSaving) setConfirmOpen(false);
          }}
        >
          <DialogTitle>Remove Alvys credentials?</DialogTitle>
          <DialogContent>
            <Typography>{REMOVE_WARNING}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              disabled={isSaving}
              onClick={(): void => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              disabled={isSaving}
              onClick={handleRemove}
              className={classes.removeButton}
            >
              {isSaving ? "Removing…" : "Remove credentials"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default withStyles(styles)(AlvysIntegrationPanel);
